import { useTranslation } from 'react-i18next';

import { FeaturedEvent } from '@/feature/lobby-sbk/components/FeaturedBets/types';
import { useFormatPeriod } from '@/hooks/use-format-period';
import { isEventLive, resolveEventTimeInfo } from '@/utils/event-time';

import { BetSlipEvent } from '../types';

export type EventHeaderData = {
    matchup: string;
    timeInfo: string;
    isLive: boolean;
};

/**
 * Returns structured event header data. Event header is dependent on different sports and statuses.
 * Live events will show the current score and time of the match, while upcoming events will show the team names and start time.
 */
export const useFormatEventHeader = (event: BetSlipEvent | FeaturedEvent | undefined): EventHeaderData | undefined => {
    const { t } = useTranslation('event_time');
    const periodTime = useFormatPeriod({
        event,
        periodName: event?.event_details?.period_name,
        gameClock: event?.event_details?.game_clock,
        sportSpecific: event?.event_details?.sport_specific,
    });

    if (!event) {
        return undefined;
    }

    const isLive = isEventLive(event);
    const timeInfo = resolveEventTimeInfo({ periodTime, isLive, startTime: event.start_time, liveLabel: t('live') });

    let matchup = `${event.away_team.short_code} @ ${event.home_team.short_code}`;

    if (isLive) {
        const awayTeamDetails = [`${event?.away_team?.short_code ?? ''}`, `${event?.event_details?.away_score ?? ''}`]
            .filter(Boolean)
            .join(' ');
        const homeTeamDetails = [`${event?.home_team?.short_code ?? ''}`, `${event?.event_details?.home_score ?? ''}`]
            .filter(Boolean)
            .join(' ');
        matchup = `${awayTeamDetails} @ ${homeTeamDetails}`;
    }

    return {
        matchup,
        timeInfo,
        isLive,
    };
};

export const formatEventHeaderString = (data: EventHeaderData | undefined): string => {
    if (!data) {
        return '';
    }
    return [data.matchup, data.timeInfo].filter(Boolean).join(' • ');
};
